import { useMemo } from "react";
import { Calculator } from "lucide-react";
import { calculateMoraInterest, DEFAULT_PRESCRIBED_RATE, formatZAR } from "@/lib/moraInterest";

type Props = {
  values: Record<string, string>;
};

/**
 * Live mora-interest preview shown inside the Letter of Demand modal.
 * Updates as the user types the capital and due date. Rate defaults to
 * the current prescribed rate but can be overridden via an `interest_rate`
 * field if the form ever exposes one.
 */
export const MoraInterestPreview = ({ values }: Props) => {
  const calc = useMemo(() => {
    const amount = Number((values.amount ?? "").toString().replace(/[^\d.]/g, ""));
    const dueDate = values.due_date;
    const rate = Number(values.interest_rate ?? DEFAULT_PRESCRIBED_RATE) || DEFAULT_PRESCRIBED_RATE;
    if (!amount || !dueDate) return null;
    return calculateMoraInterest(amount, dueDate, rate);
  }, [values.amount, values.due_date, values.interest_rate]);

  if (!calc) return null;

  return (
    <div className="rounded-input border border-yellow/40 bg-yellow-bg p-4">
      <div className="flex items-start gap-2.5">
        <Calculator className="mt-0.5 h-4 w-4 flex-shrink-0 text-brass-dark" strokeWidth={1.8} />
        <div className="flex-1">
          <div className="text-[0.74rem] font-semibold uppercase tracking-wider text-brass-dark">
            Mora interest preview
          </div>
          <p className="mt-0.5 text-[0.72rem] font-light leading-[1.5] text-brass-dark/80">
            Calculated under the Prescribed Rate of Interest Act 55 of 1975 at {calc.rate.toFixed(2)}% p.a.
          </p>
          {calc.days === 0 ? (
            <p className="mt-2 text-[0.78rem] text-brass-dark">
              The debt is not yet in mora. Interest will start to accrue from the day after the due date.
            </p>
          ) : (
            <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-[0.78rem]">
              <dt className="text-brass-dark/80">Capital</dt>
              <dd className="text-right font-medium text-foreground">{formatZAR(calc.capital)}</dd>
              <dt className="text-brass-dark/80">Days in mora</dt>
              <dd className="text-right font-medium text-foreground">{calc.days}</dd>
              <dt className="text-brass-dark/80">Interest accrued</dt>
              <dd className="text-right font-medium text-foreground">{formatZAR(calc.interest)}</dd>
              <dt className="border-t border-yellow/40 pt-1.5 font-semibold text-foreground">Total now due</dt>
              <dd className="border-t border-yellow/40 pt-1.5 text-right font-bold text-foreground">
                {formatZAR(calc.total)}
              </dd>
            </dl>
          )}
        </div>
      </div>
    </div>
  );
};
